/**
 * ADR-0019 — `/job-reports`'s URL, which holds the GENERATED report: what the preview shows and
 * what PDF / Excel download. The filter bar edits a draft beside it, and Generate is what
 * moves the draft into the URL — so a shared link reopens exactly the report that was printed.
 *
 * Hand-written rather than zod, because `zod` is not a dependency of `apps/web` (the note
 * `attendance.tsx` and `meal-allowance.tsx` carry).
 */
import dayjs from 'dayjs';

export type JobReportKindParam = 'daily' | 'monthly';

/** A TYPE ALIAS so it carries an implicit index signature (see `AgentsSearch`). */
export type JobReportsSearch = {
  /** Absent is `daily`. */
  kind?: 'monthly';
  /** The daily report's date — present iff a daily report has been generated. */
  date?: string;
  /** The monthly report's period — present iff a monthly report has been generated. */
  periodId?: string;
  /** Comma-separated team ids; absent is EVERY team. */
  teams?: string;
};

const ISO = /^\d{4}-\d{2}-\d{2}$/;

const str = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() !== '' ? v.trim() : undefined);

export function validateJobReportsSearch(search: Record<string, unknown>): JobReportsSearch {
  const date = str(search['date']);
  return {
    kind: search['kind'] === 'monthly' ? 'monthly' : undefined,
    date: date !== undefined && ISO.test(date) && dayjs(date).isValid() ? date : undefined,
    periodId: str(search['periodId']),
    teams: str(search['teams']),
  };
}

/** `"a,b,a"` → `["a","b"]`; absent → `[]`, which the screen shows as "All teams". */
export function teamIdsOf(teams: string | undefined): string[] {
  if (teams === undefined) return [];
  return [...new Set(teams.split(',').map((t) => t.trim()).filter((t) => t !== ''))];
}

/** `[]` → absent (every team), so "All" is never a list that tomorrow's new team is missing from. */
export function teamsParam(ids: readonly string[]): string | undefined {
  return ids.length === 0 ? undefined : ids.join(',');
}

/** What the filter bar holds before Generate. */
export interface JobReportDraft {
  kind: JobReportKindParam;
  date: string;
  periodId: string | undefined;
  teamIds: string[];
}

/** The URL a draft generates. Only the chosen kind's own key is kept. */
export function searchOf(draft: JobReportDraft): JobReportsSearch {
  return draft.kind === 'daily'
    ? { date: draft.date, teams: teamsParam(draft.teamIds) }
    : { kind: 'monthly', periodId: draft.periodId, teams: teamsParam(draft.teamIds) };
}

/** True when the URL already holds this draft — Generate then refreshes rather than navigates. */
export function sameSearch(a: JobReportsSearch, b: JobReportsSearch): boolean {
  return (
    (a.kind ?? 'daily') === (b.kind ?? 'daily') &&
    a.date === b.date &&
    a.periodId === b.periodId &&
    teamIdsOf(a.teams).sort().join(',') === teamIdsOf(b.teams).sort().join(',')
  );
}
